import { motion } from "framer-motion";
import { TreePalm, Flower2, Users, ClipboardCheck } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const practices = [
  {
    icon: TreePalm,
    title: "Male Palm Selection",
    description:
      "Selected healthy male palms are used for pollen collection to ensure genetic quality.",
  },
  {
    icon: Flower2,
    title: "Controlled Pollination",
    description:
      "Well-maintained dwarf female palms are used for controlled pollination procedures.",
  },
  {
    icon: Users,
    title: "Trained Supervision",
    description:
      "All operations carried out under CPCRI trained personnel supervision.",
  },
  {
    icon: ClipboardCheck,
    title: "Best Practices",
    description:
      "Following recommended nursery management practices for quality seedlings.",
  },
];

export default function PracticesSection() {
  return (
    <section
      className="py-20 md:py-32 bg-[#2F5233] relative overflow-hidden"
      data-testid="practices-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-sm font-medium tracking-widest uppercase text-[#81B29A] mb-4 block">
            Our Process
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6 tracking-tight"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            Hybrid Seedling Practices
          </h2>
          <p className="text-white/80 text-lg leading-relaxed">
            The nursery follows careful and controlled selection practices for
            producing hybrid coconut seedlings.
          </p>
        </motion.div>

        {/* Practices Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {practices.map((practice, index) => (
            <motion.div
              key={practice.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
                delay: index * 0.1,
              }}
              className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl border border-white/20 hover:bg-white/15 transition-colors duration-300"
            >
              <div className="w-14 h-14 bg-[#E07A5F] rounded-2xl flex items-center justify-center mb-6">
                <practice.icon className="w-7 h-7 text-white" />
              </div>
              <h3
                className="text-xl font-semibold text-white mb-3"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                {practice.title}
              </h3>
              <p className="text-white/70 leading-relaxed text-sm">
                {practice.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
